import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, BookOpenIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { FireIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface CourseCardProps {
    course: {
        id: string;
        name: string;
        nameEn?: string;
        avatar?: string | null;
        scheduleInfo: string;
        status: string;
        location?: string;
        description?: string;
        hasTodayClass?: boolean;
    };
    index: number;
    showStatusBadge?: boolean;
    programId?: string;
    programName?: string;
}

const CourseCard: React.FC<CourseCardProps> = ({
    course,
    index,
    showStatusBadge = true,
    programId,
    programName
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    // 获取状态标签的样式类
    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'completed':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'cancelled':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    // 处理导航
    const handleAttendanceClick = () => {
        try {
            if (!course.id) {
                console.error('导航失败: 课程ID为空');
                return;
            }
            // 导航到attendancelist并且滚动到顶部
            // window.open(`/#/attendancelist?courseId=${course.id}`, '_blank');
            const programParam = programId ? `&programId=${programId}` : '';
            navigate(`/attendancelist?courseId=${course.id}${programParam}`);
        } catch (error) {
            console.error('导航到考勤页面失败:', error);
        }
    };

    const handleStudentsClick = () => {
        try {
            if (!course.id) {
                console.error('导航失败: 课程ID为空');
                return;
            }
            navigate(`/students?courseId=${course.id}${programId ? `&programId=${programId}` : ''}`);
        } catch (error) {
            console.error('导航到学生列表页面失败:', error);
        }
    };

    return (
        <motion.div
            key={course.id}
            className={`rounded-xl overflow-hidden shadow-lg ${course.hasTodayClass
                ? 'bg-gradient-to-br from-blue-600/80 to-indigo-700/80 border border-blue-400/30'
                : 'bg-white/10 backdrop-blur-sm border border-white/10'
                }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            onClick={handleAttendanceClick}
        >
            {/* 课程封面 */}
            <div className="h-40 relative bg-blue-500/30">
                {course.avatar ? (
                    <img
                        src={course.avatar}
                        alt={course.name}
                        className="h-full w-full object-contain"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-800 to-indigo-900 flex items-center justify-center">
                        <BookOpenIcon className="h-16 w-16 text-blue-200 opacity-50" />
                    </div>
                )}

                {/* 今日课程标记 */}
                {course.hasTodayClass && (
                    <div className="absolute top-3 right-3 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
                        <FireIcon className="h-3 w-3 mr-1 animate-pulse" />
                        {t('profile.today')}
                    </div>
                )}

                {/* 状态标记 - 仅在需要时显示 */}
                {showStatusBadge && (
                    <div className="absolute top-3 left-3">
                        <span className={`px-2 py-1 text-xs rounded-full border ${getStatusBadgeClass(course.status)}`}>
                            {t(`profile.courseStatus.${course.status}`)}
                        </span>
                    </div>
                )}
            </div>

            {/* 课程信息 */}
            <div className="p-4">
                <h3 className="text-lg font-semibold text-white mb-2">{i18n.language === 'zh' ? course.name : course.nameEn}</h3>

                {programName && (
                    <div className="mb-2">
                        <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-blue-400/20 text-blue-100 border border-blue-400/30">
                            {programName}
                        </span>
                    </div>
                )}

                {course.location && (
                    <p className="text-sm text-blue-100 mb-3">
                        <span className="opacity-70">{t('profile.location')}:</span> {course.location}
                    </p>
                )}

                {/* 课程安排 */}
                {course.scheduleInfo && (
                    <div className="mb-3">
                        <p className="text-sm text-blue-100 mb-2">{t('course.schedule')}:</p>
                        <div className="min-h-[80px] px-2 py-1 text-xs bg-blue-500/30 text-blue-100 rounded border border-blue-400/30">
                            <div className="flex items-start">
                                <CalendarIcon className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                                <span className="line-clamp-2">{course.scheduleInfo}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* 操作按钮 */}
                <div className="flex gap-2 mt-4">
                    <button
                        
                        className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors flex items-center justify-center"
                    >
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        {t('profile.markAttendance')}
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

export default CourseCard; 